import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { Fragment, useState } from "react";
import { Cell, Label, Pie, PieChart } from "recharts";

import { ChartFrame, FilterBar } from "@/components/data";
import { applyFilters, parseFilters } from "@/lib/filters";

import { Chip, FlagChip } from "../../chips";
import { readShared } from "../../filters";
import { OptionLink, TabLink } from "../../links";
import { TabPage } from "../../PageFrame";
import { CHART } from "../charts";
import { phaseNo, reachedNo } from "../criteria_completion_phase/Page";
import { NO_PHASE, PHASE_ORDER } from "../drop_per_phase/Page";
import type { PageProps } from "../index";
import { dateGB, f2, Footnote, MIN_STANDARD, money, OptionFlags, PageHead } from "../kit";
import { Block, BlockGrid, ListTable, NotScoredNote } from "../overview";
import { evidenceSearch } from "../ranking_table/Page";
import { ItemHeaderTiles, ItemQuoteTables } from "./Quotes";
import { TopN } from "./TopN";
import { AllItemsLink, ItemSummaryPage, ItemWindowTiles, WindowBar } from "./Summary";

export interface TopOption {
  option_id: string;
  option_name: string;
  phase: string | null;
  dropped: boolean;
  rating: number;
  criteria_count: number;
  low_evidence: boolean;
  weakest_feature: string | null;
  weakest_rating: number | null;
}

export interface OverviewCompletionRow {
  option_id: string;
  option_name: string;
  phase: string | null;
  dropped: boolean;
  criteria_phase: string;
  expected_n: number;
  scored_n: number;
  completion: number | null;
}

export interface CostLine {
  option_id: string;
  option_name: string;
  variant: string | null;
  cost_gbp: number;
}

/** A product line: an ingredient's variant, else a "/ Unit" cost type (C1 rev 1.5). */
export interface UnitLine {
  line: string;
  options_n: number;
}

/** One option's latest unit price on one line, with its yearly figure (C1 rev 1.5). */
export interface UnitOption {
  line: string;
  option_id: string;
  option_name: string;
  variant: string | null;
  cost_type: string;
  unit_gbp: number;
  yearly_gbp: number | null;
}

/** The item's latest purchase on one variant (C1 rev 1.5, ingredients only). */
export interface Purchase {
  line: string;
  supplier: string | null;
  price_gbp: number | null;
  purchase_date: string | null;
  incoterm: string | null;
  quantity: number | null;
}

export interface ItemOverview {
  item_id: string;
  item_name: string | null;
  funnel: string | null;
  /** Options that match the shared filters. */
  in_scope_n: number;
  /** Every option of the item, filters ignored. The three parts add up to `total`. */
  status_split: { active: number; dropped: number; archived: number; total: number };
  per_phase: Array<{ phase: string; options_n: number }>;
  top_options: TopOption[];
  not_scored_n: number;
  completion: OverviewCompletionRow[];
  cost: {
    cost_type: string;
    item_average_gbp: number | null;
    costed_options: number;
    offers_any_type_n: number;
    options: CostLine[];
    /** C1 rev 1.5. Optional: a rev 1.3 response has none, and the block then draws as before. */
    lines?: UnitLine[];
    line?: string | null;
    line_unit?: string | null;
    unit_options?: UnitOption[];
    purchases?: Purchase[];
  };
}

/** Said in place of the page when the item holds no option at all. */
export const NO_OPTIONS = "No options for this item yet. Add them through /source.";

export interface SplitPart {
  label: string;
  value: number;
  percent: number;
  /** Swatch class and chart colour of the part. */
  swatch: string;
  fill: string;
}

/** Active, dropped and archived as counts and whole-number shares of every option of the item. */
export function statusSplit(split: ItemOverview["status_split"]): SplitPart[] {
  const percent = (value: number) => (split.total > 0 ? Math.round((value / split.total) * 100) : 0);
  return [
    { label: "Active", value: split.active, percent: percent(split.active), swatch: "bg-ks-purple", fill: CHART.purple },
    { label: "Dropped", value: split.dropped, percent: percent(split.dropped), swatch: "bg-ks-grey-1", fill: CHART.grey1 },
    { label: "Archived", value: split.archived, percent: percent(split.archived), swatch: "bg-ks-purple-2", fill: CHART.purple2 },
  ];
}

/** The five funnel phases in order, 0 where the item has none; any other phase value C1 sends comes after. */
export function phaseBars(perPhase: ItemOverview["per_phase"]): { rows: Array<{ phase: string; count: number }>; max: number } {
  const counts = new Map(perPhase.map((row) => [row.phase ?? NO_PHASE, row.options_n]));
  const fixed = PHASE_ORDER.filter((phase) => phase !== "ARCHIVE");
  const extra = Array.from(counts.keys()).filter((phase) => !fixed.includes(phase)).sort();
  const rows = [...fixed, ...extra].map((phase) => ({ phase, count: counts.get(phase) ?? 0 }));
  return { rows, max: Math.max(1, ...rows.map((row) => row.count)) };
}

/** A completion cell: a share scored, "Not reached" for a phase past the option's own, or nothing to score. */
export type CompletionCell = { kind: "percent"; percent: number } | { kind: "not_reached" } | { kind: "none" };

export interface CompletionLine {
  option_id: string;
  option_name: string;
  cells: Record<string, CompletionCell>;
}

/** One line per option, in C1's order, one column per criteria phase. */
export function completionGrid(rows: OverviewCompletionRow[]): { phases: string[]; lines: CompletionLine[] } {
  const lines: CompletionLine[] = [];
  const byId = new Map<string, CompletionLine>();
  const phases = new Set<string>();
  for (const row of rows) {
    let line = byId.get(row.option_id);
    if (!line) {
      line = { option_id: row.option_id, option_name: row.option_name, cells: {} };
      byId.set(row.option_id, line);
      lines.push(line);
    }
    phases.add(row.criteria_phase);
    line.cells[row.criteria_phase] =
      row.expected_n === 0
        ? { kind: "none" }
        : phaseNo(row.criteria_phase) > reachedNo(row.phase)
          ? { kind: "not_reached" }
          : { kind: "percent", percent: Math.round((row.completion ?? 0) * 100) };
  }
  return { phases: Array.from(phases).sort(), lines };
}

/** 2 decimals for per-unit costs, none for yearly totals: read off the dearest figure on show. */
export function costDp(cost: ItemOverview["cost"]): 0 | 2 {
  const highest = Math.max(0, cost.item_average_gbp ?? 0, ...cost.options.map((line) => line.cost_gbp));
  return highest < 20 ? 2 : 0;
}

function topColumns(itemId: string): ColumnDef<TopOption, unknown>[] {
  return [
    { id: "rank", header: "#", cell: ({ row }) => <span className="text-ks-grey-1">{row.index + 1}</span> },
    {
      id: "option",
      header: "Option",
      cell: ({ row }) => (
        // The row itself is clickable; a click on the name must open the option, not the row's target.
        <span onClick={(event) => event.stopPropagation()} onKeyDown={(event) => event.stopPropagation()}>
          <OptionLink optionId={row.original.option_id} itemId={itemId} from="item_overview">
            {row.original.option_name}
          </OptionLink>
        </span>
      ),
    },
    { id: "rating", header: "Rating", meta: { align: "right" }, cell: ({ row }) => f2(row.original.rating) },
    { id: "criteria", header: "Criteria", meta: { align: "right" }, cell: ({ row }) => `${row.original.criteria_count} criteria` },
    { id: "flags", header: "Flags", cell: ({ row }) => <OptionFlags row={{ ...row.original, not_scored: false }} /> },
  ];
}

export function TopOptions({ data, onOpen }: { data: ItemOverview; onOpen: (option: TopOption) => void }) {
  return (
    <Block title="Top options">
      {data.top_options.length > 0 ? (
        <TopN rows={data.top_options}>
          {(shown) => (
            <ListTable<TopOption>
              columns={topColumns(data.item_id)}
              rows={shown}
              caption="The three best rated options"
              headerHidden
              onRowClick={onOpen}
            />
          )}
        </TopN>
      ) : (
        <NotScoredNote />
      )}
      <Footnote>
        <TabLink tabKey="ranking_table">See full ranking</TabLink>
      </Footnote>
    </Block>
  );
}

export function WeakestFeature({ data }: { data: ItemOverview }) {
  return (
    <Block title="Weakest feature vs 7.0">
      {data.top_options.length > 0 ? (
        <TopN rows={data.top_options}>
          {(shown) => (
        <ul className="list-disc pl-6 text-sm">
          {shown.map((option) => (
            <li key={option.option_id} className="my-1.5">
              <b>
                <OptionLink optionId={option.option_id} itemId={data.item_id} from="item_overview">
                  {option.option_name}
                </OptionLink>
              </b>
              {" — "}
              {option.low_evidence ? (
                <>
                  <FlagChip kind="low_evidence" /> too few scores to judge
                </>
              ) : option.weakest_feature === null ? (
                "—"
              ) : (
                <>
                  {option.weakest_feature} <b>{f2(option.weakest_rating)}</b>{" "}
                  {option.weakest_rating !== null && option.weakest_rating < MIN_STANDARD ? <Chip tone="warn">below 7.0</Chip> : null}
                </>
              )}
            </li>
          ))}
        </ul>
          )}
        </TopN>
      ) : (
        <NotScoredNote />
      )}
      <Footnote>
        <TabLink tabKey="feature_performance_average">See Feature Performance</TabLink>
      </Footnote>
    </Block>
  );
}

function completionColumns(itemId: string, phases: string[]): ColumnDef<CompletionLine, unknown>[] {
  return [
    {
      id: "option",
      header: "Option",
      cell: ({ row }) => (
        <OptionLink optionId={row.original.option_id} itemId={itemId} from="item_overview">
          {row.original.option_name}
        </OptionLink>
      ),
    },
    ...phases.map(
      (phase): ColumnDef<CompletionLine, unknown> => ({
        id: `phase:${phase}`,
        header: phase,
        meta: { align: "right" },
        cell: ({ row }) => {
          const cell = row.original.cells[phase];
          if (!cell || cell.kind === "none") return "—";
          if (cell.kind === "not_reached") return <Chip tone="muted">Not reached</Chip>;
          return `${cell.percent}%`;
        },
      }),
    ),
  ];
}

export function ScoringCompletion({ data }: { data: ItemOverview }) {
  const { phases, lines } = completionGrid(data.completion);
  return (
    <Block title="Scoring completion">
      {lines.length > 0 ? (
        <TopN rows={lines}>
          {(shown) => (
            <ListTable<CompletionLine>
              columns={completionColumns(data.item_id, phases)}
              rows={shown}
              caption="Share of each phase's criteria scored, for the best rated options"
              wrap
            />
          )}
        </TopN>
      ) : (
        <p className="text-xs text-ks-grey-1">No criteria for this funnel yet.</p>
      )}
      <Footnote>
        <TabLink tabKey="criteria_completion_phase">See Criteria Completion / Phase</TabLink>
      </Footnote>
    </Block>
  );
}

function costColumns(itemId: string, dp: 0 | 2): ColumnDef<CostLine, unknown>[] {
  return [
    {
      id: "option",
      header: "Option",
      cell: ({ row }) => (
        <OptionLink optionId={row.original.option_id} itemId={itemId} from="item_overview">
          {/* An ingredient option can carry one offer per variant; the variant tells its lines apart. */}
          {row.original.variant ? `${row.original.option_name} · ${row.original.variant}` : row.original.option_name}
        </OptionLink>
      ),
    },
    { id: "cost", header: "Cost", meta: { align: "right" }, cell: ({ row }) => money(row.original.cost_gbp, dp) },
  ];
}

/** A line as the page shows it: a cost type's " / Unit" suffix is dropped ("Full bottle sleeve / Unit" → "Full bottle sleeve"). */
export function lineLabel(line: string): string {
  return line.replace(/ \/ Unit$/, "");
}

/** The line on screen: the one in the URL when the item has it, else C1's default (the most quoted). */
export function chosenLine(cost: ItemOverview["cost"], picked: string | null): string | null {
  const lines = cost.lines ?? [];
  if (picked !== null && lines.some((entry) => entry.line === picked)) return picked;
  return cost.line ?? lines[0]?.line ?? null;
}

export type CostSortColumn = "unit" | "yearly";
export interface CostSort {
  column: CostSortColumn;
  direction: "asc" | "desc";
}
export const DEFAULT_COST_SORT: CostSort = { column: "unit", direction: "asc" };

/** A header click: the same column flips, another column starts lowest first. */
export function nextCostSort(current: CostSort, column: CostSortColumn): CostSort {
  if (current.column === column) return { column, direction: current.direction === "asc" ? "desc" : "asc" };
  return { column, direction: "asc" };
}

export type CostRow =
  | { kind: "offer"; option_id: string; option_name: string; variant: string | null; unit_gbp: number | null; yearly_gbp: number | null }
  | { kind: "current"; supplier: string | null; unit_gbp: number | null; yearly_gbp: null; purchase: Purchase };

/**
 * Every option on the chosen line plus the item's current purchase on it, in the chosen order.
 * A missing figure sorts last both ways; equal figures keep C1's order (cheapest, then name).
 */
export function costRows(cost: ItemOverview["cost"], line: string | null, sort: CostSort): CostRow[] {
  if (line === null) return [];
  const rows: CostRow[] = (cost.unit_options ?? [])
    .filter((row) => row.line === line)
    .map((row) => ({
      kind: "offer",
      option_id: row.option_id,
      option_name: row.option_name,
      variant: row.variant,
      unit_gbp: row.unit_gbp,
      yearly_gbp: row.yearly_gbp,
    }));
  const purchase = (cost.purchases ?? []).find((entry) => entry.line === line);
  if (purchase) {
    rows.push({ kind: "current", supplier: purchase.supplier, unit_gbp: purchase.price_gbp, yearly_gbp: null, purchase });
  }
  const value = (row: CostRow) => (sort.column === "unit" ? row.unit_gbp : row.yearly_gbp);
  const sign = sort.direction === "asc" ? 1 : -1;
  return rows
    .map((row, index) => ({ row, index }))
    .sort((a, b) => {
      const x = value(a.row);
      const y = value(b.row);
      if (x === null || y === null) return x === y ? a.index - b.index : x === null ? 1 : -1;
      return x === y ? a.index - b.index : (x - y) * sign;
    })
    .map(({ row }) => row);
}

function unitColumns(itemId: string, unit: string): ColumnDef<CostRow, unknown>[] {
  return [
    {
      id: "option",
      header: "Option",
      cell: ({ row }) =>
        row.original.kind === "current" ? (
          <span className="inline-flex flex-wrap items-center gap-1.5">
            Current · {row.original.supplier ?? "—"} <Chip tone="info">Current</Chip>
          </span>
        ) : (
          <OptionLink optionId={row.original.option_id} itemId={itemId} from="item_overview">
            {row.original.variant ? `${row.original.option_name} · ${row.original.variant}` : row.original.option_name}
          </OptionLink>
        ),
    },
    { id: "unit", header: `Per unit (${unit})`, meta: { align: "right", sortable: true }, cell: ({ row }) => money(row.original.unit_gbp, 2) },
    { id: "yearly", header: "Yearly", meta: { align: "right", sortable: true }, cell: ({ row }) => money(row.original.yearly_gbp, 0) },
  ];
}

/** The rev 1.5 block: one line at a time, every option on it, per unit and yearly, beside the current purchase. */
export function UnitCostBlock({
  data,
  picked,
  onPick,
  sort,
  onSort,
}: {
  data: ItemOverview;
  picked: string | null;
  onPick: (line: string | null) => void;
  sort: CostSort;
  onSort: (column: CostSortColumn) => void;
}) {
  const { cost } = data;
  const lines = cost.lines ?? [];
  const line = chosenLine(cost, picked);
  const fallback = chosenLine(cost, null) ?? "";
  const rows = costRows(cost, line, sort);
  const current = rows.find((row): row is Extract<CostRow, { kind: "current" }> => row.kind === "current");
  const pill =
    lines.length > 1 ? (
      <FilterBar
        layout="inline"
        searchable
        filters={[
          {
            name: "line",
            label: "Line",
            kind: "choice",
            anyLabel: `Most quoted: ${lineLabel(fallback)}`,
            // The most quoted line is the empty choice, so it is not listed twice.
            options: lineOptions(lines, fallback),
          },
        ]}
        // While another line is picked the shell bar also shows its "Clear 1 filter", which returns to the most quoted.
        values={{ line: line === picked && picked !== fallback ? picked : null }}
        onChange={(next) => onPick(typeof next.line === "string" ? next.line : null)}
      />
    ) : null;
  return (
    <Block title="Cost" aside={pill}>
      <TopN rows={rows}>
        {(shown) => (
          <ListTable<CostRow>
            columns={unitColumns(data.item_id, cost.line_unit ?? "per unit")}
            rows={shown}
            caption={`Unit and yearly cost of every option on ${lineLabel(line ?? "")}`}
            sort={{ id: sort.column, direction: sort.direction }}
            onSort={(id) => onSort(id as CostSortColumn)}
            rowClassName={(row) => (row.kind === "current" ? "bg-ks-purple-4" : undefined)}
          />
        )}
      </TopN>
      <Footnote>
        {current?.purchase.purchase_date
          ? `Current = latest purchase, ${dateGB(current.purchase.purchase_date)}${current.purchase.incoterm ? `, ${current.purchase.incoterm}` : ""}. `
          : ""}
        Prices as quoted, not landed.
      </Footnote>
      <Footnote>
        Item average {money(cost.item_average_gbp, 0)} · <TabLink tabKey="sourcing_funnel">See Sourcing Funnel</TabLink>
      </Footnote>
    </Block>
  );
}

/** The Line pill's choices: every line but the most quoted, which is the empty choice, each with its option count. */
export function lineOptions(lines: Array<{ line: string; options_n: number }>, fallback: string): Array<{ value: string; label: string }> {
  return lines
    .filter((entry) => entry.line !== fallback)
    .map((entry) => ({ value: entry.line, label: `${lineLabel(entry.line)} (${entry.options_n})` }));
}

export function CostBlock({ data }: { data: ItemOverview }) {
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const navigate = useNavigate();
  const [sort, setSort] = useState<CostSort>(DEFAULT_COST_SORT);
  const { cost } = data;
  if ((cost.lines ?? []).length > 0) {
    const raw = parseFilters(search).line;
    return (
      <UnitCostBlock
        data={data}
        picked={typeof raw === "string" && raw !== "" ? raw : null}
        // Read in the browser only: every line is already in this response, so a switch asks C1 nothing.
        onPick={(line) =>
          void navigate({ search: ((previous: Record<string, unknown>) => applyFilters(previous, { line })) as never, replace: true } as never)
        }
        sort={sort}
        onSort={(column) => setSort((previous) => nextCostSort(previous, column))}
      />
    );
  }
  const dp = costDp(cost);
  const others = cost.offers_any_type_n;
  return (
    <Block title={`Cost (${cost.cost_type})`}>
      {cost.options.length > 0 ? (
        <>
          <TopN rows={cost.options}>
            {(shown) => (
              <ListTable<CostLine>
                columns={costColumns(data.item_id, dp)}
                rows={shown}
                caption="Cost of the best rated options that have an offer"
                headerHidden
              />
            )}
          </TopN>
          <Footnote>
            Item average {money(cost.item_average_gbp, dp)} · <TabLink tabKey="sourcing_funnel">See Sourcing Funnel</TabLink>
          </Footnote>
        </>
      ) : others > 0 ? (
        <Footnote>
          No offers under this cost type. {others} option{others === 1 ? " has" : "s have"} an offer under another one ·{" "}
          <TabLink tabKey="sourcing_funnel">See Sourcing Funnel</TabLink>
        </Footnote>
      ) : (
        <Footnote>No offers yet for this item.</Footnote>
      )}
    </Block>
  );
}

/** Only tests pass a size: inside `ChartFrame` the chart takes its size from the frame. */
export function StatusDonut({ parts, total, width, height }: { parts: SplitPart[]; total: number; width?: number; height?: number }) {
  return (
    <PieChart width={width} height={height}>
      <Pie
        data={parts}
        dataKey="value"
        nameKey="label"
        innerRadius={35}
        outerRadius={53}
        startAngle={90}
        endAngle={-270}
        stroke="none"
        isAnimationActive={false}
      >
        {parts.map((part) => (
          <Cell key={part.label} fill={part.fill} />
        ))}
        <Label value={total} position="center" fontSize={18} fontWeight="bold" fill={CHART.ink} />
      </Pie>
    </PieChart>
  );
}

export function OptionsBlock({ data }: { data: ItemOverview }) {
  const parts = statusSplit(data.status_split);
  const { rows, max } = phaseBars(data.per_phase);
  return (
    <Block title={`Options · ${data.status_split.total} total`}>
      <div className="flex flex-wrap items-center gap-4">
        <div className="w-[120px] shrink-0">
          <ChartFrame title="Options by status" height={120} pointCount={data.status_split.total}>
            <StatusDonut parts={parts} total={data.status_split.total} />
          </ChartFrame>
        </div>
        <TopN rows={parts}>
          {(shown) => (
            <ul className="flex flex-col gap-1 text-sm">
              {shown.map((part) => (
                <li key={part.label} className="flex items-center gap-2">
                  <i aria-hidden="true" className={`inline-block size-3 rounded-[2px] ${part.swatch}`} />
                  {part.label} <b>{part.value}</b> · {part.percent}%
                </li>
              ))}
            </ul>
          )}
        </TopN>
      </div>
      <h3 className="mt-3.5 mb-2 text-sm font-bold">Per phase</h3>
      <TopN rows={rows}>
        {(shown) => (
          <div className="grid grid-cols-[max-content_1fr_max-content] items-center gap-x-3 gap-y-1.5 text-sm">
            {shown.map((row) => (
              <Fragment key={row.phase}>
                <span>{row.phase}</span>
                <div className="h-3 rounded-[2px] bg-ks-purple" style={{ width: `${(row.count / max) * 100}%` }} />
                <span className="text-right tabular-nums">{row.count}</span>
              </Fragment>
            ))}
          </div>
        )}
      </TopN>
      <Footnote>Dropped options are counted in the split, not in a phase.</Footnote>
    </Block>
  );
}

export function ItemOverviewBody({ data, onOpen }: { data: ItemOverview; onOpen: (option: TopOption) => void }) {
  return (
    <BlockGrid>
      <CostBlock data={data} />
      <TopOptions data={data} onOpen={onOpen} />
      <WeakestFeature data={data} />
      <ScoringCompletion data={data} />
      <OptionsBlock data={data} />
    </BlockGrid>
  );
}

export function ItemDetailPage({ tab, areaName }: PageProps) {
  const navigate = useNavigate();
  const params = useParams({ strict: false }) as { area?: string; module?: string };
  const open = (option: TopOption) =>
    void navigate({
      to: "/app/$area/$module/$tab",
      params: { area: params.area ?? "sourcing", module: params.module ?? "sourcing_funnel", tab: "criteria_ranking" },
      search: ((previous: Record<string, unknown>) => evidenceSearch(previous, option.option_id)) as never,
    } as never);
  return (
    <TabPage<ItemOverview>
      tab={tab}
      areaName={areaName}
      isEmpty={(data) => data.in_scope_n === 0}
      notFound={{ when: (data) => data === null || data.status_split.total === 0, message: NO_OPTIONS }}
      head={(data) => (
        <>
          <AllItemsLink />
          <PageHead title={tab.label} sub={data?.item_name ? `${data.item_name} · one-screen picture for the decision` : undefined} />
          <WindowBar />
          <ItemHeaderTiles />
          <ItemWindowTiles />
          <ItemQuoteTables />
        </>
      )}
    >
      {(data) => <ItemOverviewBody data={data} onOpen={open} />}
    </TabPage>
  );
}

/** Item = All is the executive summary; a chosen item is that item's page. */
export function ItemOverviewPage(props: PageProps) {
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  return readShared(search).item === null ? <ItemSummaryPage {...props} /> : <ItemDetailPage {...props} />;
}
